import { useEffect, useState } from "react";

import {
  archiveClassroom,
  changeClassroomMember,
  changeClassroomProposal,
  fetchClassroom,
  fetchClassrooms,
  fetchClassroomStatistics,
  fetchCollections,
  joinClassroom,
  saveClassroom,
} from "./api";
import { navigate } from "./navigation";
import { subscribeScoreInvalidations } from "./score";
import type {
  ClassroomDetail,
  ClassroomStatisticsResponse,
  ClassroomSummary,
  CollectionSummary,
} from "./types";

function Pager({ page, pages, onChange }: { page: number; pages: number; onChange: (page: number) => void }) {
  if (pages <= 1) return null;
  return (
    <div className="history-pagination">
      <button className="button" type="button" disabled={page <= 1} onClick={() => onChange(page - 1)}>← Précédente</button>
      <span>Page {page} sur {pages}</span>
      <button className="button" type="button" disabled={page >= pages} onClick={() => onChange(page + 1)}>Suivante →</button>
    </div>
  );
}

export function ClassroomsPage({ persona }: { persona: "student" | "teacher" }) {
  const [rooms, setRooms] = useState<ClassroomSummary[] | null>(null);
  const [roomPage, setRoomPage] = useState(1);
  const [roomPages, setRoomPages] = useState(1);
  const [selected, setSelected] = useState<ClassroomDetail | null>(null);
  const [memberPage, setMemberPage] = useState(1);
  const [proposalPage, setProposalPage] = useState(1);
  const [statistics, setStatistics] = useState<ClassroomStatisticsResponse | null>(null);
  const [statisticsPage, setStatisticsPage] = useState(1);
  const [collections, setCollections] = useState<CollectionSummary[]>([]);
  const [name, setName] = useState("");
  const [joinCode, setJoinCode] = useState("");
  const [username, setUsername] = useState("");
  const [collectionId, setCollectionId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadRooms = async (page = roomPage) => {
    const payload = await fetchClassrooms(page);
    setRooms(payload.classrooms);
    setRoomPage(payload.pagination.page);
    setRoomPages(payload.pagination.pages);
  };

  const loadDetail = async (id: string, nextMemberPage = memberPage, nextProposalPage = proposalPage) => {
    const room = await fetchClassroom(id, nextMemberPage, nextProposalPage);
    setSelected(room);
    setMemberPage(room.pagination.students?.page ?? 1);
    setProposalPage(room.pagination.proposals.page);
    if (room.can_manage) setStatistics(await fetchClassroomStatistics(id, statisticsPage));
    else setStatistics(null);
    return room;
  };

  useEffect(() => {
    let active = true;
    void fetchClassrooms(roomPage)
      .then((payload) => {
        if (!active) return;
        setRooms(payload.classrooms);
        setRoomPages(payload.pagination.pages);
      })
      .catch((reason: unknown) => {
        if (active) setError(reason instanceof Error ? reason.message : "Classes indisponibles.");
      });
    return () => { active = false; };
  }, [roomPage]);

  useEffect(() => {
    if (persona !== "teacher") return;
    let active = true;
    void fetchCollections(false, 1, 100)
      .then((payload) => {
        if (active) setCollections(payload.collections.filter((item) => item.is_published));
      })
      .catch(() => undefined);
    return () => { active = false; };
  }, [persona]);

  useEffect(() => subscribeScoreInvalidations(() => {
    if (!selected?.can_manage) return;
    void fetchClassroomStatistics(selected.id, statisticsPage)
      .then(setStatistics)
      .catch(() => undefined);
  }), [selected?.can_manage, selected?.id, statisticsPage]);

  const open = async (room: ClassroomSummary) => {
    setLoading(true);
    setError(null);
    setMemberPage(1);
    setProposalPage(1);
    setStatisticsPage(1);
    try {
      const detail = await loadDetail(room.id, 1, 1);
      setName(detail.name);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Classe indisponible.");
    } finally {
      setLoading(false);
    }
  };

  const createOrRename = async () => {
    if (!name.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const room = await saveClassroom(name, selected?.can_manage ? selected.id : undefined);
      setSelected(room);
      await loadRooms();
      await loadDetail(room.id, 1, 1);
      setName(room.name);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Classe non enregistrée.");
    } finally {
      setLoading(false);
    }
  };

  const memberAction = async (action: "add" | "remove", memberUsername: string) => {
    if (!selected) return;
    setLoading(true);
    setError(null);
    try {
      await changeClassroomMember(selected.id, action, memberUsername);
      setUsername("");
      await loadRooms();
      await loadDetail(selected.id, memberPage, proposalPage);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Membre non modifié.");
    } finally {
      setLoading(false);
    }
  };

  const proposalAction = async (action: "add" | "remove", id: number) => {
    if (!selected) return;
    setLoading(true);
    setError(null);
    try {
      await changeClassroomProposal(selected.id, action, id);
      setCollectionId("");
      await loadDetail(selected.id, memberPage, proposalPage);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Proposition non modifiée.");
    } finally {
      setLoading(false);
    }
  };

  const join = async () => {
    if (!joinCode.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const id = joinCode.trim();
      await joinClassroom(id);
      await loadRooms(1);
      await loadDetail(id, 1, 1);
      setJoinCode("");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Code de classe invalide.");
    } finally {
      setLoading(false);
    }
  };

  const archive = async () => {
    if (!selected) return;
    setLoading(true);
    setError(null);
    try {
      await archiveClassroom(selected.id);
      setSelected(null);
      setStatistics(null);
      setName("");
      await loadRooms(1);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Classe non archivée.");
    } finally {
      setLoading(false);
    }
  };

  const changeMemberPage = async (page: number) => {
    if (!selected) return;
    setLoading(true);
    try { await loadDetail(selected.id, page, proposalPage); }
    finally { setLoading(false); }
  };

  const changeProposalPage = async (page: number) => {
    if (!selected) return;
    setLoading(true);
    try { await loadDetail(selected.id, memberPage, page); }
    finally { setLoading(false); }
  };

  const changeStatisticsPage = async (page: number) => {
    if (!selected) return;
    setLoading(true);
    try {
      setStatistics(await fetchClassroomStatistics(selected.id, page));
      setStatisticsPage(page);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="collection-page classrooms-page">
      <button className="back-button" type="button" onClick={() => navigate("/")}>← Retour au tableau de bord</button>
      <div className="collection-heading">
        <div><p className="eyebrow">Groupes pédagogiques</p><h1>Mes classes</h1><p>{persona === "teacher" ? "Gérez vos élèves, leurs statistiques et les séries proposées." : "Retrouvez vos enseignants et les séries proposées à votre classe."}</p></div>
        <span className="section-chip">{rooms?.length ?? 0} classe{rooms?.length === 1 ? "" : "s"} sur cette page</span>
      </div>
      {error && <div className="info-notice" role="alert">{error}</div>}
      <div className="classrooms-layout">
        <aside className="classroom-sidebar">
          {persona === "teacher" ? <div className="classroom-form"><label>Nom de la classe<input value={name} maxLength={255} onChange={(event) => setName(event.target.value)} placeholder="Ex. 5e B" /></label><button className="button button-primary" type="button" disabled={loading || !name.trim()} onClick={() => void createOrRename()}>{selected?.can_manage ? "Renommer" : "Créer la classe"}</button>{selected && <button className="button" type="button" onClick={() => { setSelected(null); setStatistics(null); setName(""); }}>Nouvelle classe</button>}</div> : <div className="classroom-form"><label>Code de classe<input value={joinCode} onChange={(event) => setJoinCode(event.target.value)} placeholder="Identifiant transmis par l’enseignant" /></label><button className="button button-primary" type="button" disabled={loading || !joinCode.trim()} onClick={() => void join()}>Rejoindre</button></div>}
          <div className="classroom-list">{rooms === null ? <p>Chargement…</p> : rooms.length ? rooms.map((room) => <button className={selected?.id === room.id ? "active" : ""} type="button" key={room.id} onClick={() => void open(room)}><strong>{room.name}</strong><small>{room.teacher.display_name} · {room.students_count} élève{room.students_count > 1 ? "s" : ""}</small></button>) : <p>Aucune classe active pour le moment.</p>}</div>
          <Pager page={roomPage} pages={roomPages} onChange={setRoomPage} />
        </aside>
        <div className="classroom-content">
          {!selected ? <div className="empty-state"><strong>Sélectionnez une classe</strong><p>Son contenu, ses propositions et ses statistiques apparaîtront ici.</p></div> : <><header><p className="eyebrow">Code {selected.id}</p><h2>{selected.name}</h2><p>Enseignant : {selected.teacher.display_name}</p>{selected.can_manage && <button className="button danger-button" type="button" disabled={loading} onClick={() => void archive()}>Archiver la classe</button>}</header>{selected.can_manage ? <><section><h3>Élèves ({selected.students_count})</h3><div className="classroom-inline-form"><input aria-label="Pseudo exact de l’élève" value={username} onChange={(event) => setUsername(event.target.value)} placeholder="Pseudo exact de l’élève" /><button className="button" type="button" disabled={loading || !username.trim()} onClick={() => void memberAction("add", username)}>Ajouter</button></div><ul className="classroom-members">{selected.students?.map((student) => <li key={student.id}><span><strong>{student.display_name}</strong><small>@{student.username}</small></span><button type="button" disabled={loading} onClick={() => void memberAction("remove", student.username)}>Retirer</button></li>)}</ul>{selected.pagination.students && <Pager page={selected.pagination.students.page} pages={selected.pagination.students.pages} onChange={(page) => void changeMemberPage(page)} />}</section><section><h3>Séries proposées</h3><div className="classroom-inline-form"><select aria-label="Série publiée" value={collectionId} onChange={(event) => setCollectionId(event.target.value)}><option value="">Choisir une série publiée</option>{collections.map((collection) => <option key={collection.id} value={collection.id}>{collection.title}</option>)}</select><button className="button" type="button" disabled={loading || !collectionId} onClick={() => void proposalAction("add", Number(collectionId))}>Proposer</button></div><ul className="classroom-members">{selected.proposals.map((proposal) => <li key={proposal.collection_id}><span><strong>{proposal.title}</strong><small>{proposal.recipients} destinataire(s)</small></span><button type="button" disabled={loading} onClick={() => void proposalAction("remove", proposal.collection_id)}>Retirer</button></li>)}</ul><Pager page={selected.pagination.proposals.page} pages={selected.pagination.proposals.pages} onChange={(page) => void changeProposalPage(page)} /></section><section><div className="classroom-section-heading"><h3>Statistiques agrégées</h3>{statistics?.individual_results_allowed && <a className="button" href={`/api/exports/classrooms/${selected.id}/statistics.pdf`}>Exporter le détail PDF</a>}</div>{statistics?.results.length ? <div className="table-scroll"><table className="classroom-statistics"><thead><tr><th>Série</th><th>Mode</th><th>Élèves</th><th>Parties</th><th>Réussites</th><th>Score</th><th>Temps</th></tr></thead><tbody>{statistics.results.map((row) => <tr key={`${row.collection_id}-${row.game}`}><td>{row.collection__title}</td><td>{row.game_label}</td><td>{row.students}</td><td>{row.played}</td><td>{row.won}</td><td>{row.score}</td><td>{row.time}s</td></tr>)}</tbody></table></div> : <div className="empty-state"><p>Aucune statistique disponible pour cette classe.</p></div>}{statistics && <Pager page={statistics.pagination.page} pages={statistics.pagination.pages} onChange={(page) => void changeStatisticsPage(page)} />}</section></> : <section><h3>Séries proposées</h3>{selected.proposals.length ? <div className="collection-grid">{selected.proposals.map((proposal) => <article className="collection-tile" key={proposal.collection_id}><p className="eyebrow">{proposal.subject}</p><h2>{proposal.title}</h2><button className="button button-primary" type="button" onClick={() => navigate(proposal.path!)}>Ouvrir</button></article>)}</div> : <div className="empty-state"><p>Aucune série proposée pour le moment.</p></div>}<Pager page={selected.pagination.proposals.page} pages={selected.pagination.proposals.pages} onChange={(page) => void changeProposalPage(page)} /></section>}</>}
        </div>
      </div>
    </section>
  );
}
